// components/CaptchaHost/index.tsx
//
// Shows the captcha widget the gateway asks for. Mounted once by the auth
// layout. While it is mounted, withCaptcha hands each 428/400 challenge here
// and the request waits for the token; closing the challenge cancels that
// request quietly.
//
// Only a visible widget (`size: normal`, a checkbox) gets our sheet. In
// invisible mode the provider opens its own puzzle pop-up over the page, with
// its own close button, so a sheet of ours would only be an empty box behind
// it: the widget gets a bare container instead.
//
// The widget is rendered only for a challenge, never on page load: the
// provider and key come from the response, and the route may not need one.

import { useEffect, useRef, useState } from 'react';
import { Stack, Typography, useTheme } from '@rabex-kit/rabex-ui';
import CustomModal from '../CustomModal';
import { registerCaptchaSolver } from '../../services/captcha/broker';
import { getCaptchaAdapter } from '../../services/captcha/adapters';
import { CaptchaChallenge, CaptchaError } from '../../services/captcha/types';

type Pending = {
  challenge: CaptchaChallenge;
  resolve: (token: string) => void;
  reject: (error: CaptchaError) => void;
};

const CaptchaHost = () => {
  const { palette } = useTheme();
  const [pending, setPending] = useState<Pending | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const mode = useRef(palette.mode);
  mode.current = palette.mode;
  // Leaving the page mid-challenge must not leave its request hanging.
  const pendingRef = useRef<Pending | null>(null);
  pendingRef.current = pending;

  useEffect(
    () =>
      registerCaptchaSolver(
        (challenge) =>
          new Promise<string>((resolve, reject) => {
            if (!getCaptchaAdapter(challenge.provider)) {
              // Worth noticing: the gateway enabled a provider this build lacks.
              console.warn(
                `[captcha] no adapter for provider "${challenge.provider}"`
              );
              reject(new CaptchaError('unsupported', challenge.provider));
              return;
            }
            setPending({ challenge, resolve, reject });
          })
      ),
    []
  );

  useEffect(
    () => () => pendingRef.current?.reject(new CaptchaError('cancelled')),
    []
  );

  useEffect(() => {
    if (!pending || !container) return undefined;

    const adapter = getCaptchaAdapter(pending.challenge.provider)!();
    let active = true;
    const { site_key, options } = pending.challenge;

    adapter
      // The gateway's theme wins; otherwise match the app.
      .solve(container, site_key, { theme: mode.current, ...options })
      .then(
        (token) => active && pending.resolve(token),
        (error) =>
          active &&
          pending.reject(
            error instanceof CaptchaError
              ? error
              : new CaptchaError('widget', String(error))
          )
      )
      .finally(() => {
        if (active) setPending(null);
      });

    return () => {
      active = false;
      // A provider's failure must stay inside the captcha: thrown here it would
      // unmount the whole app.
      try {
        adapter.reset();
      } catch (e) {
        console.warn('[captcha] reset failed', e);
      }
      // The next challenge renders into a clean container.
      container.replaceChildren();
    };
  }, [pending, container]);

  const cancel = () => {
    pending?.reject(new CaptchaError('cancelled'));
    setPending(null);
  };

  const invisible = pending?.challenge.options?.size !== 'normal';

  // Never display:none: the provider builds its hidden widget in here.
  if (pending && invisible) return <div ref={setContainer} />;

  return (
    <CustomModal
      isOpen={!!pending}
      width={412}
      title="تأیید امنیتی"
      onClose={cancel}
      mountBodyOnOpen
      body={
        <Stack alignItems="center" gap={4} pb={2}>
          <Typography
            variant="body5"
            color={palette.textColor[400]}
            textAlign="center">
            برای ادامه، لطفاً کپچا را تکمیل کنید.
          </Typography>
          {/* Never hidden: the provider may show its puzzle in here at any time. */}
          <div ref={setContainer} />
        </Stack>
      }
    />
  );
};

export default CaptchaHost;
